import { AuthenticationStore } from "@istari-frontend/auth";
import { useExperimentalFeature } from "@istari-frontend/experimental-features";
import { getEnv } from "@istari-frontend/shared";
import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useAuth } from "react-oidc-context";
import { createBrowserRouter, Navigate, Outlet, ScrollRestoration, useLocation } from "react-router";

import { AiChatFloatingWidget } from "~/ai-chat/feature/ai-chat-floating-widget";
import { AiChatProvider } from "~/ai-chat/feature/use-ai-chat";
import { ChatViewContextProvider } from "~/ai-chat/feature/use-chat-view-context";
import { InfosecBanner } from "~/control-tags/feature/infosec-banner";
import { settingsPageElements } from "~/pages/Main/Settings/settings-page-elements";
import { RequireSession, SessionProvider, surfaceEntries } from "~/session";
import { settingsEntries } from "~/settings/feature/settings-entries";
import { SettingsEntryRoute } from "~/settings/feature/settings-entry-route";

import App from "./App";
import ErrorBoundaryDisplay from "./components/ErrorBoundaryDisplay";
import { ItarBanner } from "./components/itar-banner";
import { TopBannerOffsetEffect, useTopBannerOffset } from "./components/top-banner-offset";
import { JobEventsHost } from "./jobs/feature/job-events-host";
import { NotificationToastHost } from "./notifications/feature/notification-toast-host";
import { adminPageElementForEntry } from "./pages/Main/Admin/admin-page-elements";
import { LegacyArtifactRedirect } from "./pages/Main/Artifact/legacy-artifact-redirect";
import { systemHomeTabElements } from "./pages/Main/SystemHome/system-home-tab-elements";
import { AdminIndexRedirect } from "./router/admin-index-redirect";
import { ResourcesDashboardGuard, SystemsDashboardGuard } from "./router/dashboard-experimental-guards";
import { LegacyFilesRedirect } from "./router/legacy-files-redirect";
import { PageTitle } from "./router/page-title";
import { resourcesDriveRouteEntries } from "./router/resources-drive-route-entries";
import { createResourcesLibraryRouteElements } from "./router/resources-library-route-elements";
import { RootRedirect } from "./router/root-redirect";
import { SettingsIndexRedirect } from "./router/settings-index-redirect";
import { TenantUsersRedirect } from "./router/tenant-users-redirect";
import { routes } from "./routes";
import { SystemEditGuard } from "./systems/feature/system-edit-guard";
import { createSystemHomeTabRoutes } from "./systems/feature/system-home-tab-routes";
import { UploadProvider } from "./upload/upload-provider";

const SystemHomePage = React.lazy(() => import("./pages/Main/SystemHome"));

const systemHomeTabRoutes = createSystemHomeTabRoutes(systemHomeTabElements);

const SignIn = React.lazy(() => import("./pages/Authentication/SignIn"));

const AllSystems = React.lazy(() => import("./pages/Main/AllSystems"));
const SystemsDashboard = React.lazy(() => import("./pages/Main/SystemsDashboard"));
const AiChatPage = React.lazy(() => import("~/ai-chat/feature/ai-chat-page"));

const AllModelsNew = React.lazy(() => import("./pages/Main/AllModelsNew/all-models-new"));
const LibrariesPage = React.lazy(() => import("./pages/Main/Libraries/libraries-page"));
const ResourcesDashboard = React.lazy(() => import("./pages/Main/ResourcesDashboard"));
const ResourcesDrive = React.lazy(() => import("./pages/Main/ResourceLibrary"));
const Jobs = React.lazy(() => import("./pages/Main/Jobs"));
const SettingsPageLayout = React.lazy(() => import("./pages/Main/Settings"));

const Comment = React.lazy(() => import("./pages/Main/CommentPage"));
const SystemDetailsPage = React.lazy(() => import("./pages/Main/System"));
const FunctionAuthCallback = React.lazy(() => import("./pages/Main/FunctionAuthCallback"));

const AdminPageLayout = React.lazy(() => import("./pages/Main/Admin/admin-page-layout"));
const ConsolePageLayout = React.lazy(() => import("./pages/Main/Console/console-page-layout"));
const AdminSecureConnectionSettingsPage = React.lazy(
  () => import("./pages/Main/Admin/secure-connection-settings-page"),
);
const AdminSecureConnectionSyncStatusPage = React.lazy(
  () => import("./pages/Main/Admin/secure-connection-sync-status-page"),
);
const ConsoleUsersPage = React.lazy(() => import("./pages/Main/Console/console-users-page"));
const ConsoleUserPage = React.lazy(() => import("./pages/Main/Console/console-user-page"));
const ConsoleTenantsPage = React.lazy(() => import("./pages/Main/Console/console-tenants-page"));
const ConsoleTenantPage = React.lazy(() => import("./pages/Main/Console/console-tenant-page"));

// The page each console registry entry renders; the registry itself stays free of page imports.
const consolePages: Record<string, React.ReactNode> = {
  tenants: <ConsoleTenantsPage />,
  tenant: <ConsoleTenantPage />,
  "console-users": <ConsoleUsersPage />,
  "console-user": <ConsoleUserPage />,
};
const AgentManagementPage = React.lazy(() => import("./pages/Main/AgentManagement/agent-management-page"));
const CreateChangeRequestPage = React.lazy(() => import("./pages/Main/ChangeRequest/create-change-request-page"));
const ChangeRequestDetailPage = React.lazy(() => import("./pages/Main/ChangeRequest/change-request-detail-page"));
const NotificationsPage = React.lazy(() => import("./pages/Main/Notifications"));

const SearchResultsPage = React.lazy(() => import("./search/feature"));
const PageNotFound = React.lazy(() => import("./components/PageNotFound"));

const LineageDevPage = import.meta.env.DEV ? React.lazy(() => import("./pages/Dev/lineage-dev-page")) : null;

function StackedTopBanners() {
  const { infosecBannerOffset } = useTopBannerOffset();
  return (
    <>
      <TopBannerOffsetEffect />
      <InfosecBanner />
      <ItarBanner topOffset={infosecBannerOffset} />
    </>
  );
}

export function UserMemPagesLayout() {
  const { enabled: isAiChatEnabled } = useExperimentalFeature("ai-chat");

  const content = (
    <SessionProvider>
      <StackedTopBanners />
      <NotificationToastHost />
      <JobEventsHost />
      <UploadProvider>
        <Outlet />
      </UploadProvider>
      {isAiChatEnabled && <AiChatFloatingWidget />}
    </SessionProvider>
  );

  if (!isAiChatEnabled) {
    return content;
  }

  return (
    <AiChatProvider>
      <ChatViewContextProvider>{content}</ChatViewContextProvider>
    </AiChatProvider>
  );
}

function AppWithErrorBoundary() {
  return (
    <ErrorBoundary FallbackComponent={ErrorBoundaryDisplay}>
      <App />
      {/* Keyed by location.key so independent history entries (incl. back/forward) restore their own scroll. */}
      <ScrollRestoration getKey={(location) => location.key} />
    </ErrorBoundary>
  );
}

function AuthManager() {
  const location = useLocation();
  const auth = useAuth();
  const AS = AuthenticationStore();
  const redirectPath = AS.consumeRedirectPath();

  return auth.isAuthenticated ? (
    <>{redirectPath ? <Navigate to={redirectPath} replace /> : <Outlet />}</>
  ) : (
    <Navigate to={routes.SignIn} state={{ from: location }} replace />
  );
}

const pageNotFoundElement = (
  <>
    <PageTitle title="Page Not Found" />
    <PageNotFound />
  </>
);

const { resourcesPage, resourcesDrivePage } = createResourcesLibraryRouteElements({
  AllModelsNew,
  ResourcesDrive,
});

export const router = createBrowserRouter(
  [
    {
      path: routes.FunctionAuthCallback,
      element: (
        <>
          <PageTitle title="Function Authentication" />
          <FunctionAuthCallback />
        </>
      ),
    },
    {
      path: routes.Root,
      element: <AppWithErrorBoundary />,
      children: [
        {
          path: routes.SignIn,
          element: (
            <>
              <PageTitle title="Sign In" />
              <SignIn />
            </>
          ),
        },
        {
          element: <AuthManager />,
          children: [
            {
              element: <UserMemPagesLayout />,
              children: [
                {
                  path: "/",
                  element: <RootRedirect />,
                },
                {
                  path: "/home",
                  element: <RootRedirect replace />,
                },
                {
                  path: routes.Systems,
                  element: (
                    <>
                      <PageTitle title="Systems" />
                      <AllSystems />
                    </>
                  ),
                },
                {
                  path: routes.SystemsDashboard,
                  element: (
                    <SystemsDashboardGuard>
                      <PageTitle title="Systems" />
                      <SystemsDashboard />
                    </SystemsDashboardGuard>
                  ),
                },
                {
                  path: routes.AiChat,
                  element: (
                    <>
                      <PageTitle title="Istari AI Chat" />
                      <AiChatPage />
                    </>
                  ),
                },
                {
                  path: routes.SystemHome,
                  element: (
                    <SystemsDashboardGuard>
                      <SystemHomePage />
                    </SystemsDashboardGuard>
                  ),
                  children: [
                    ...systemHomeTabRoutes,
                    {
                      path: routes.SystemHomeBranchScope,
                      children: systemHomeTabRoutes,
                    },
                  ],
                },
                {
                  path: routes.ResourcesDashboard,
                  element: (
                    <ResourcesDashboardGuard>
                      <PageTitle title="Resources" />
                      <ResourcesDashboard />
                    </ResourcesDashboardGuard>
                  ),
                },
                {
                  path: routes.Resources,
                  element: resourcesPage,
                },
                {
                  path: `${routes.Resources}/:modelId/:revisionId?`,
                  element: resourcesPage,
                },
                {
                  path: `${routes.Resources}/:modelId/:revisionId?/artifact/:artifactId/:artifactRevisionId?`,
                  element: resourcesPage,
                },
                {
                  path: routes.ResourcesUnparentedArtifact,
                  element: resourcesPage,
                },
                ...resourcesDriveRouteEntries(resourcesDrivePage),
                ...[routes.Libraries, routes.LibraryDetails, routes.PackageDetails, routes.Package].map((path) => ({
                  path,
                  element: (
                    <>
                      <PageTitle title="Libraries" />
                      <LibrariesPage />
                    </>
                  ),
                })),
                {
                  path: "/files/*",
                  element: <LegacyFilesRedirect />,
                },
                {
                  path: routes.FilesConnectedResource,
                  element: resourcesPage,
                },
                {
                  path: routes.Jobs,
                  element: (
                    <>
                      <PageTitle title="Jobs" />
                      <Jobs />
                    </>
                  ),
                },
                {
                  path: routes.JobDetails,
                  element: (
                    <>
                      <PageTitle title="Job Details" />
                      <Jobs />
                    </>
                  ),
                },
                {
                  path: routes.Search,
                  element: (
                    <>
                      <PageTitle title="Search" />
                      <SearchResultsPage />
                    </>
                  ),
                },
                {
                  path: routes.Admin,
                  element: (
                    <>
                      <PageTitle title="Admin" />
                      <AdminPageLayout />
                    </>
                  ),
                  children: [
                    {
                      index: true,
                      element: <AdminIndexRedirect />,
                    },
                    ...surfaceEntries("tenant").map((entry) => ({
                      path: entry.path,
                      element: (
                        <RequireSession guards={entry.guards}>
                          <PageTitle title={entry.title} />
                          {adminPageElementForEntry(entry.id)}
                        </RequireSession>
                      ),
                    })),
                    { path: routes.AdminAiModels, element: <Navigate to={`${routes.AdminAi}?tab=models`} replace /> },
                    {
                      path: routes.AdminAiSettings,
                      element: <Navigate to={`${routes.AdminAi}?tab=instructions`} replace />,
                    },
                    {
                      path: routes.AdminSecureConnectionSettings,
                      element: (
                        <>
                          <PageTitle title="Connection Settings" />
                          <AdminSecureConnectionSettingsPage />
                        </>
                      ),
                    },
                    {
                      path: routes.AdminSecureConnectionSyncStatus,
                      element: (
                        <>
                          <PageTitle title="Sync Status" />
                          <AdminSecureConnectionSyncStatusPage />
                        </>
                      ),
                    },
                  ],
                },
                {
                  path: routes.Console,
                  element: (
                    <>
                      <PageTitle title="Platform Admin Console" />
                      <ConsolePageLayout />
                    </>
                  ),
                  children: [
                    {
                      index: true,
                      element: <Navigate to={routes.ConsoleTenants} replace />,
                    },
                    { path: routes.ConsoleTenantUsers, element: <TenantUsersRedirect /> },
                    ...surfaceEntries("console").map((entry) => ({
                      path: entry.path,
                      element: (
                        <RequireSession guards={entry.guards}>
                          <PageTitle title={entry.title} />
                          {consolePages[entry.id]}
                        </RequireSession>
                      ),
                    })),
                  ],
                },
                {
                  path: "/agent-management",
                  element: <Navigate to={routes.Agents} replace />,
                },
                {
                  path: "/admin/agents",
                  element: <Navigate to={routes.Agents} replace />,
                },
                {
                  path: "/admin/agent",
                  element: <Navigate to={routes.Agents} replace />,
                },
                {
                  path: "/admin/agent-management",
                  element: <Navigate to={routes.Agents} replace />,
                },
                {
                  path: routes.Agents,
                  element: (
                    <>
                      <PageTitle title="Agents" />
                      <AgentManagementPage />
                    </>
                  ),
                },
                {
                  path: routes.Notifications,
                  element: (
                    <>
                      <PageTitle title="Notifications" />
                      <NotificationsPage />
                    </>
                  ),
                },
                {
                  path: routes.Settings,
                  element: (
                    <>
                      <PageTitle title="Settings" />
                      <SettingsPageLayout />
                    </>
                  ),
                  children: [
                    {
                      index: true,
                      element: <SettingsIndexRedirect />,
                    },
                    ...settingsEntries.map((entry) => ({
                      path: entry.path,
                      element: (
                        <SettingsEntryRoute entry={entry}>
                          <PageTitle title={entry.title} />
                          {settingsPageElements[entry.id]}
                        </SettingsEntryRoute>
                      ),
                    })),
                  ],
                },
                {
                  path: routes.CreateChangeRequest,
                  element: (
                    <SystemEditGuard message="You need edit access to create a change request.">
                      <PageTitle title="New Change Request" />
                      <CreateChangeRequestPage />
                    </SystemEditGuard>
                  ),
                },
                {
                  path: routes.ChangeRequestDetail,
                  element: (
                    <>
                      <PageTitle title="Change Request" />
                      <ChangeRequestDetailPage />
                    </>
                  ),
                },
                {
                  path: routes.SystemDetails,
                  element: (
                    <>
                      <PageTitle title="System Details" />
                      <SystemDetailsPage />
                    </>
                  ),
                },
                {
                  path: routes.Artifact,
                  element: <LegacyArtifactRedirect />,
                },
                {
                  path: routes.CommentDetails,
                  element: (
                    <>
                      <PageTitle title="Comment Details" />
                      <Comment />
                    </>
                  ),
                },
                ...(import.meta.env.DEV && LineageDevPage
                  ? [{ path: "/dev/lineage", element: <LineageDevPage /> }]
                  : []),
                { path: routes.NotFound, element: pageNotFoundElement },
                { path: "*", element: pageNotFoundElement },
              ],
            },
          ],
        },
      ],
    },
  ],
  {
    basename: getEnv("BASE_URL"),
  },
);
